import { useState, useCallback } from 'react'

import { Box, NumberInput, TextInput, Divider } from '@mantine/core'
import axios from 'axios'

import IconCheckmark from 'src/icons/IconCheckmark'

import PlacesAutocomplete from '../../../ApplicantDetailsPage/tabs/ApplicationTab/components/AddressInput/PlacesAutocomplete'

const MILES_TO_METERS_CONVERSION_FACTOR = 1609.34
const MIN_RADIUS_MILES = 0
const MAX_RADIUS_MILES = 30
const DEFAULT_RADIUS_MILES = 5

const milesToMeters = (miles: number) =>
  miles * MILES_TO_METERS_CONVERSION_FACTOR

interface ZipcodeFilterProps {
  selectedZipcodes: string[]
  newZipcode?: string
  setNewZipcode?: (value: string) => void
  handleAddZipcode?: (zipcode: string) => void
  handleRemoveZipcode?: (zipcode: string) => void
  suggestedZipcodes?: string[]
  setSuggestedZipcodes?: (zipcodes: string[]) => void
  handleAddSuggestedZipcode?: (zipcode: string) => void
  handleAddAllSuggestedZipcodes?: () => void
}

const ZipcodeFilter = ({
  selectedZipcodes,
  newZipcode,
  setNewZipcode,
  handleAddZipcode,
  handleRemoveZipcode,
  suggestedZipcodes,
  setSuggestedZipcodes,
  handleAddSuggestedZipcode,
  handleAddAllSuggestedZipcodes,
}: ZipcodeFilterProps) => {
  // Ensure selectedZipcodes is always an array
  const safeSelectedZipcodes = Array.isArray(selectedZipcodes)
    ? selectedZipcodes
    : []
  const [radiusMiles, setRadiusMiles] = useState(DEFAULT_RADIUS_MILES)

  const handleCityChange = useCallback(
    async (place: { location: { latitude: number; longitude: number } }) => {
      if (!place || !place.location) return

      try {
        const { latitude, longitude } = place.location
        const apiKey = process.env.GOOGLE_MAPS_API_KEY
        const url = 'https://places.googleapis.com/v1/places:searchNearby'

        const requestBody = {
          locationRestriction: {
            circle: {
              center: {
                latitude,
                longitude,
              },
              radius: milesToMeters(radiusMiles),
            },
          },
          includedTypes: ['postal_code'],
          maxResultCount: 20,
        }

        const response = await axios.post(url, requestBody, {
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask': '*',
          },
        })

        const data = response.data
        if (data.places && data.places.length > 0) {
          const suggestedZipcodes = data.places.map(
            (place: { displayName?: { text: string } }) =>
              place.displayName?.text
          )
          setSuggestedZipcodes?.(suggestedZipcodes)
        }
      } catch (e) {
        console.error('Error fetching nearby zipcodes:', e)
      }
    },
    [radiusMiles, setSuggestedZipcodes]
  )

  return (
    <div className="space-y-3 p-2">
      {/* Manual zipcode input */}
      <div className="space-y-2">
        <TextInput
          placeholder="Enter Postal Code"
          value={newZipcode || ''}
          onChange={(e) => {
            setNewZipcode?.(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              e.stopPropagation()
              if (newZipcode?.trim()) {
                handleAddZipcode?.(newZipcode.trim())
              }
            }
          }}
          size="xs"
        />
      </div>

      {safeSelectedZipcodes && safeSelectedZipcodes.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-doubleNickel-gray-700">
            Selected postal codes:
          </div>
          <div className="space-y-1">
            {safeSelectedZipcodes.map((zipcode) => (
              <Box
                key={zipcode}
                className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-100"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleRemoveZipcode?.(zipcode)
                }}
              >
                {zipcode}
                <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
              </Box>
            ))}
          </div>
        </div>
      )}

      {/* Divider */}
      <Divider label="OR" labelPosition="center" size="xs" />

      {/* Places autocomplete for nearby zipcodes */}
      <div className="space-y-4">
        <div className="flex flex-row items-end gap-2">
          <div className="flex-1">
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
              className="flex flex-col items-baseline gap-1"
            >
              <div className="text-xs font-medium text-doubleNickel-gray-500">
                Find nearby postal codes:
              </div>
              <PlacesAutocomplete
                placeholder="Search for a city or address"
                value={undefined}
                handleVerboseChange={handleCityChange}
                types={[
                  'locality',
                  'street_address',
                  'subpremise',
                  'postal_code',
                  'route',
                ]}
                size="xs"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-xs font-medium text-doubleNickel-gray-500">
              Radius:
            </div>
            <NumberInput
              className="w-16"
              hideControls
              min={MIN_RADIUS_MILES}
              max={MAX_RADIUS_MILES}
              value={radiusMiles}
              onChange={(val) => {
                if (typeof val === 'number') {
                  setRadiusMiles(val)
                } else if (val === null) {
                  setRadiusMiles(DEFAULT_RADIUS_MILES)
                }
              }}
              size="xs"
            />
          </div>
        </div>

        {/* Suggested zipcodes */}
        {suggestedZipcodes && suggestedZipcodes.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-row items-center justify-between text-xs font-medium text-doubleNickel-gray-700">
              Click to add nearby postal codes:
              {suggestedZipcodes.filter(
                (zipcode) => !safeSelectedZipcodes.includes(zipcode)
              ).length > 0 && (
                <Box
                  className="text-doubleNickel-brand-400 hover:cursor-pointer hover:underline"
                  onClick={() => {
                    handleAddAllSuggestedZipcodes?.()
                  }}
                >
                  Add all
                </Box>
              )}
            </div>
            <div className="space-y-1">
              {suggestedZipcodes
                .filter((zipcode) => !safeSelectedZipcodes.includes(zipcode))
                .map((zipcode) => (
                  <Box
                    key={zipcode}
                    className="flex cursor-pointer flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-100"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleAddSuggestedZipcode?.(zipcode)
                    }}
                  >
                    {zipcode}
                  </Box>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ZipcodeFilter
